<template>
  <div class="min-h-screen bg-gray-50">
    <!-- For authenticated users -->
    <div v-if="authStore.isAuthenticated" class="flex h-screen overflow-hidden">
      <!-- Mobile overlay for sidebar -->
      <div
        v-if="authStore.isAdmin && sidebarExpanded && isMobileView"
        @click="closeSidebar"
        class="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
      ></div>

      <!-- Sidebar component - only shown for admin users -->
      <Sidebar
        v-if="authStore.isAdmin"
        ref="sidebarRef"
        @sidebar-toggle="updateSidebarState"
        :is-mobile-view="isMobileView"
      />

      <!-- Main content wrapper -->
      <div class="flex flex-col flex-1 overflow-hidden">
        <!-- Fixed header -->
        <header
          class="bg-white shadow-sm z-20 border-b border-gray-200 flex-shrink-0"
        >
          <div
            class="flex justify-between items-center px-2 sm:px-4 py-2 sm:py-3"
            :class="{ 'max-w-[1280px] mx-auto w-full': !authStore.isAdmin }"
          >
            <div class="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
              <!-- Toggle button - only for admin -->
              <button
                v-if="authStore.isAdmin"
                @click="toggleSidebar"
                class="text-slate-600 hover:text-slate-900 p-1 sm:p-2 rounded hover:bg-gray-100 flex-shrink-0"
              >
                <i class="pi pi-bars text-lg sm:text-xl"></i>
              </button>

              <!-- Logo for non-admin users -->
              <div v-if="!authStore.isAdmin" class="flex items-center flex-shrink-0">
                <img :src="logoURL" alt="Logo" class="h-12 sm:h-16 lg:h-20 w-auto mr-2 sm:mr-3" />
              </div>

              <!-- Page title with responsive sizing -->
              <h1 class="text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold text-slate-800 truncate">
                {{ pageTitle }}
              </h1>
            </div>

            <div class="flex items-center gap-1 sm:gap-2 lg:gap-3">
              <div class="text-xs sm:text-sm md:text-base text-slate-600 hidden sm:block">
                <i v-if="authStore.isAdmin" class="pi pi-calendar mr-1"></i>
                <span v-if="authStore.isAdmin">{{ formattedDate }} | </span>
                {{ formatProperString(authStore.usernamex) }}
              </div>

              <!-- Show username on mobile -->
              <div class="text-xs text-slate-600 sm:hidden truncate max-w-[100px]">
                {{ formatProperString(authStore.usernamex) }}
              </div>

              <div class="flex items-center gap-1 sm:gap-2">
                <!-- Dropdown menu container -->
                <div class="relative">
                  <!-- Avatar that toggles dropdown -->
                  <div @click="toggleProfileDropdown" class="cursor-pointer">
                    <Avatar
                      :label="userInitials"
                      :size="isMobileView ? 'normal' : 'large'"
                      shape="circle"
                      class="bg-indigo-600 text-sm sm:text-base cursor-pointer"
                    />
                  </div>

                  <!-- Dropdown menu -->
                  <div
                    v-show="profileDropdownVisible"
                    class="absolute right-0 top-full mt-1 w-36 sm:w-60 bg-white border border-gray-200 rounded shadow-lg z-50"
                  >
                    <ul class="py-1 text-xs sm:text-sm text-gray-700">
                      <li>
                        <a
                          href="#"
                          class="block px-3 sm:px-4 py-2 hover:bg-gray-100"
                          @click.prevent="ViewProfile"
                        >
                          <i class="pi pi-user mr-2"></i>View Profile
                        </a>
                      </li>
                      <li>
                        <a
                          href="#"
                          class="block px-3 sm:px-4 py-2 hover:bg-gray-100"
                          @click.prevent="ChangePassword"
                        >
                          <i class="pi pi-user-edit mr-2"></i>Change Password
                        </a>
                      </li>
                      <li>
                        <a
                          href="#"
                          class="block px-3 sm:px-4 py-2 hover:bg-gray-100"
                          @click.prevent="logout"
                        >
                          <i class="pi pi-sign-out mr-2"></i>Logout
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        <!-- Main content area with scroll -->
        <main
          v-if="authStore.isAuthenticated"
          class="flex-1 overflow-y-auto bg-gray-50"
        >
          <div class="px-2 sm:px-4 py-4 sm:py-6 md:px-6 md:py-8">
            <!-- For non-admin users, center the content with max width -->
            <div v-if="!authStore.isAdmin" class="w-auto mx-auto">
              <Button
                v-if="pageTitle !== 'Dashboard' && !authStore.isAdmin"
                label=""
                icon="pi pi-arrow-left"
                @click="goBack"
                class="mr-2 mb-2"
                severity="secondary"
                :size="isMobileView ? 'small' : 'normal'"
              />
              <div class="bg-white rounded-lg shadow-sm p-3 sm:p-4 mt-2 md:p-6">
                <router-view />
              </div>
            </div>

            <!-- For admin users, use responsive layout -->
            <div v-else class="bg-white rounded-lg shadow-sm p-3 sm:p-4 md:p-6">
              <router-view />
            </div>
          </div>
        </main>
      </div>
    </div>

    <!-- For non-authenticated users -->
    <div v-else>
      <router-view />
    </div>

    <Dialog
      v-model:visible="showPassForm"
      modal
      :style="{ width: '25vw', minWidth: '300px' }"
      :contentStyle="{ maxHeight: '80vh', overflow: 'auto' }"
      position="top"
    >
      <ChangePass @close="showPassForm = false" />
    </Dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Sidebar from './views/admin/Sidebar.vue'
import Avatar from 'primevue/avatar'
import Button from 'primevue/button'
import ChangePass from '@/views/pages/auth/ChangePass.vue'
import { useAuthStore } from '@/auth/auth'
import logoURL from '@/assets/sanlogo.png'

const showPassForm = ref(false)
const authStore = useAuthStore()
const route = useRoute()
const router = useRouter()

const currentTime = ref(new Date())
const sidebarRef = ref(null)
const sidebarExpanded = ref(false)
const profileDropdownVisible = ref(false)

// Mobile breakpoint detection
const isMobileView = ref(window.innerWidth < 1024)

// Update sidebar state from child component
const updateSidebarState = (isExpanded) => {
  sidebarExpanded.value = isExpanded
}

// Toggle sidebar function
const toggleSidebar = () => {
  if (sidebarRef.value) {
    sidebarRef.value.toggleSidebar()
  }
}

// Close sidebar (for mobile overlay)
const closeSidebar = () => {
  if (sidebarRef.value && isMobileView.value) {
    sidebarRef.value.closeSidebar()
  }
}

const ChangePassword = () => {
  showPassForm.value = true
}

// Toggle profile dropdown
const toggleProfileDropdown = () => {
  profileDropdownVisible.value = !profileDropdownVisible.value
}

// Close dropdown when clicking outside
const handleClickOutside = (event) => {
  const dropdown = event.target.closest('.relative')
  if (!dropdown && profileDropdownVisible.value) {
    profileDropdownVisible.value = false
  }
}

// Handle window resize
const handleResize = () => {
  const wasMobile = isMobileView.value
  isMobileView.value = window.innerWidth < 1024

  if (wasMobile && !isMobileView.value && authStore.isAdmin) {
    const savedState = localStorage.getItem('sidebarExpanded')
    if (savedState !== null) {
      sidebarExpanded.value = savedState === 'true'
    } else {
      sidebarExpanded.value = true
    }
  }
}

// Update time every minute
let interval
onMounted(() => {
  interval = setInterval(() => {
    currentTime.value = new Date()
  }, 60000)

  updatePageTitle()

  if (authStore.isAdmin && !isMobileView.value) {
    const savedState = localStorage.getItem('sidebarExpanded')
    if (savedState !== null) {
      sidebarExpanded.value = savedState === 'true'
    } else {
      sidebarExpanded.value = true
    }
  }

  window.addEventListener('resize', handleResize)
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  clearInterval(interval)
  window.removeEventListener('resize', handleResize)
  document.removeEventListener('click', handleClickOutside)
})

// Computed properties
const formattedDate = computed(() => {
  return currentTime.value.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
})

const userInitials = computed(() => {
  const username = authStore.usernamex || ''
  return username
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('')
})

// Page title management
const pageTitle = ref('Dashboard')

const routeTitles = {
  '/dashboard': 'Dashboard',
  '/school': 'School Management',
  '/tree': 'SDO Officers',
  '/chat': 'Chat',
  '/calendar': 'Calendar',
  '/staff': 'Staff Management',
  '/student/studentlist': 'Student Management',
  '/classroom/classroomlist': 'Classroom Management',
  '/subject/subjectlist': 'Subject Management',
  '/classeslist': 'Classes Overview',
  '/eduqualification/eduqualificationlist': 'Educational Qualifications',
  '/certification/certificationlist': 'Certifications',
  '/adminreport': 'Reports & Analytics',
  '/leave': 'Needs Management',
  '/rights': 'Roles & Rights Management',
  '/sched': 'Schedule Management',
  '/office': 'Office Hours Management',
  '/gender/genderlist': 'Gender Management',
  '/role/rolelist': 'Role Management',
}

const goBack = () => {
  window.history.back()
}

const updatePageTitle = () => {
  pageTitle.value = routeTitles[route.path] || route.name || 'Dashboard'
}

function formatProperString(str) {
  if (!str) return ''
  return str
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

// Watch for route changes
watch(
  () => route.path,
  () => {
    updatePageTitle()
    profileDropdownVisible.value = false
  },
)

const ViewProfile = () => {
  if (authStore.staff_pid === 'null' || authStore.staff_pid === null) {
    router.push('/parent/profile/' + authStore.parent_pid.replace(/"/g, ''));
  } else {
    router.push({
      path: `/staff/profile/${authStore.staff_pid.replace(/"/g, '')}`,
      state: {
        callFromProfile: false
      }
    });
  }
}

const logout = () => {
  profileDropdownVisible.value = false
  authStore.logout()
  router.push('/auth/login')
}
</script>

<style>
html {
  font-size: 14px;
}

@media (min-width: 640px) {
  html {
    font-size: 15px;
  }
}

@media (min-width: 768px) {
  html {
    font-size: 16px;
  }
}

body {
  font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
  line-height: 1.5;
}

/* Custom scrollbar styles */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

@media (min-width: 768px) {
  ::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }
}

::-webkit-scrollbar-track {
  background: #f1f5f9;
}

::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 5px;
}

::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Mobile touch improvements */
@media (max-width: 1023px) {
  button, .cursor-pointer {
    min-height: 44px;
    min-width: 44px;
  }
}
</style>
